import * as React from "react";
import { Box, Alert, Button } from "@mui/material";
import { Calendar, Plus } from "lucide-react";
import { listSchedules, type ScheduleSummary } from "../../api/schedules";
import { Col } from "../Common/Row";
import { PageHeader } from "../Common/PageHeader";
import { EmptyState } from "../Common/EmptyState";
import { CardSkeletonList } from "../Common/CardSkeleton";
import { ScheduleRow } from "./ScheduleRow";
import { ScheduleFormDialog } from "./ScheduleFormDialog";
import { RunningTasksSection } from "./RunningTasksSection";

export function SchedulesPage() {
  const [schedules, setSchedules] = React.useState<ScheduleSummary[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [formOpen, setFormOpen] = React.useState(false);

  const refresh = React.useCallback(() => {
    setError(null);
    listSchedules()
      .then(setSchedules)
      .catch((e) => setError(String(e)));
  }, []);

  React.useEffect(() => { refresh(); }, [refresh]);

  return (
    <Box>
      <PageHeader
        title="Scheduled tasks"
        subtitle="Recurring and one-time tasks your assistant runs for you."
        actions={
          <Button
            variant="contained"
            startIcon={<Plus size={16} />}
            data-ph="header-create"
            onClick={() => setFormOpen(true)}
          >
            New task
          </Button>
        }
      />

      {/* Live background tasks (subagent jobs). Self-hides when there are
          none, so the page stays clean for users who don't run them. */}
      <RunningTasksSection />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!error && schedules === null && <CardSkeletonList />}

      {schedules !== null && schedules.length === 0 && (
        <EmptyState
          icon={Calendar}
          title="No scheduled tasks"
          description={
            "Create a recurring or one-time task your assistant runs for you — or just ask in chat (“remind me to check inventory every Friday at 9am”)."
          }
          action={
            <Button
              variant="contained"
              startIcon={<Plus size={16} />}
              data-ph="empty-create"
              onClick={() => setFormOpen(true)}
            >
              New task
            </Button>
          }
        />
      )}

      {schedules !== null && schedules.length > 0 && (
        <Col gap={1}>
          {schedules.map((s) => (
            <ScheduleRow key={s.id} schedule={s} onChanged={refresh} />
          ))}
        </Col>
      )}

      <ScheduleFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onCreated={refresh}
      />
    </Box>
  );
}
